import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  Alert,
  CircularProgress,
} from "@mui/material";

export interface ConfirmActionDialogProps {
  open: boolean;
  title: string;
  message: React.ReactNode;
  /** Optional extra details block (e.g. leave dates / total days) shown in a highlighted box. */
  details?: React.ReactNode;
  confirmLabel: string;
  cancelLabel: string;
  /** Color of the confirm button. Defaults to "primary". */
  confirmColor?: "primary" | "error" | "warning" | "success";
  onConfirm: () => void | Promise<void>;
  onClose: () => void;
}

/**
 * Generic confirmation dialog used before destructive or state-changing leave
 * actions (cancel approved leave, delete pending leave, submit leave request).
 *
 * The dialog manages its own in-flight state so the confirm button is disabled
 * and shows a spinner while the async onConfirm runs. Any error thrown by
 * onConfirm is surfaced inline and the dialog stays open.
 */
const ConfirmActionDialog: React.FC<ConfirmActionDialogProps> = ({
  open,
  title,
  message,
  details,
  confirmLabel,
  cancelLabel,
  confirmColor = "primary",
  onConfirm,
  onClose,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm();
      onClose();
    } catch (err: any) {
      setError(err?.message || "Action failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (submitting) return;
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText component="div">{message}</DialogContentText>
        {details && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              borderRadius: 1,
              bgcolor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.05)"
                  : "grey.100",
              border: (theme) => `1px solid ${theme.palette.divider}`,
            }}
          >
            {details}
          </Box>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button
          onClick={handleClose}
          disabled={submitting}
          variant="outlined"
          sx={{ textTransform: "none" }}
        >
          {cancelLabel}
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={submitting}
          variant="contained"
          color={confirmColor}
          sx={{ textTransform: "none" }}
          startIcon={
            submitting ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmActionDialog;
